import { useMemo, useState } from 'react';

import { ADDED_GROUP_LABEL } from '../constants';
import type {
  DynamicOption,
  Option,
  OptionGroup,
} from '../types';

interface UseDynamicOptionGroupsProps<O extends Option> {
  optionGroups: OptionGroup<O>[];
}

interface UseDynamicOptionGroupsReturn<O extends Option> {
  dynamicOptionGroups: OptionGroup<O | DynamicOption<O>>[];
  addDynamicOption: (option: O) => boolean;
  removeDynamicOption: (value: O['value']) => void;
}

export function useDynamicOptionGroups<O extends Option>({
  optionGroups,
}: UseDynamicOptionGroupsProps<O>): UseDynamicOptionGroupsReturn<O> {
  const [dynamicOptions, setDynamicOptions] = useState<DynamicOption<O>[]>([]);

  const removeDynamicOption = (value: O['value']) => {
    setDynamicOptions((prev) => prev.filter((o) => o.value !== value));
  };

  const addDynamicOption = (option: O): boolean => {
    const staticValues = optionGroups.flatMap((g) => g.options.map((o) => o.value));
    const dynamicValues = dynamicOptions.map((o) => o.value);

    if (staticValues.includes(option.value) || dynamicValues.includes(option.value)) {
      return false;
    }

    setDynamicOptions((prev) => [
      ...prev,
      {
        ...option,
        isDynamic: true,
        remove: () => removeDynamicOption(option.value),
      },
    ]);

    return true;
  };

  const dynamicOptionGroups = useMemo<OptionGroup<O | DynamicOption<O>>[]>(() => {
    const result: OptionGroup<O | DynamicOption<O>>[] = [];

    if (dynamicOptions.length > 0) {
      result.push({
        label: ADDED_GROUP_LABEL,
        options: dynamicOptions,
      });
    }

    result.push(
      ...optionGroups.map((group) => ({
        label: group.label,
        options: group.options,
      })),
    );

    return result;
  }, [dynamicOptions, optionGroups]);

  return {
    dynamicOptionGroups,
    addDynamicOption,
    removeDynamicOption,
  };
}
